import React, { useCallback, useEffect, useState } from "react";
import {
  Animated,
  Pressable,
  Text,
  ActivityIndicator,
  StyleSheet,
  View,
  ViewStyle,
  TextStyle,
} from "react-native";
import * as Haptics from 'expo-haptics';

interface LoadingButtonProps {
  onPress: (() => Promise<void>) | (() => void);
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
  backgroundColor?: string; // Optional background color
  style?: ViewStyle;
  textStyle?: TextStyle;
  isLoading?: boolean; // External loading state
  hapticsOnStart?: boolean; // Light haptic when press begins
  hapticsOnSuccess?: boolean; // Success haptic when onPress resolves
}

const LoadingButton: React.FC<LoadingButtonProps> = ({
  onPress,
  disabled,
  className,
  children,
  backgroundColor = "#3FC67D", // Default color,
  style,
  textStyle,
  isLoading: externalIsLoading,
  hapticsOnStart,
  hapticsOnSuccess,
}) => {
  const [internalIsLoading, setInternalIsLoading] = useState(false);
  const isLoading = externalIsLoading !== undefined ? externalIsLoading : internalIsLoading;
  const animatedValue = useState(new Animated.Value(1))[0];

  const handlePress = useCallback(async () => {
    if (isLoading) return; // Prevent duplicate presses
    
    // Only manage internal loading state if no external loading state is provided
    if (externalIsLoading === undefined) {
      setInternalIsLoading(true);
    }
    
    try {
      if (hapticsOnStart) {
        try { await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light); } catch {}
      }
      await onPress(); // Await the promise returned by onPress
      if (hapticsOnSuccess) {
        try { await Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success); } catch {}
      }
    } catch (error) {
      console.error("Error during button action:", error);
    } finally {
      if (externalIsLoading === undefined) {
        setInternalIsLoading(false);
      }
    }
  }, [isLoading, onPress, externalIsLoading, hapticsOnStart, hapticsOnSuccess]);

  const fadeToDisabledColor = () => {
    Animated.timing(animatedValue, {
      toValue: 0, // Fade to disabled
      duration: 300, // Transition duration
      useNativeDriver: false,
    }).start();
  };

  // const fadeToEnabledColor = () => {
  //   Animated.timing(animatedValue, {
  //     toValue: 1, // Fade to enabled
  //     duration: 300, // Transition duration
  //     useNativeDriver: false,
  //   }).start();
  // };

  // if (disabled || isLoading) {
  //   fadeToDisabledColor();
  // } else {
  //   fadeToEnabledColor();
  // }

  useEffect(() => {
    Animated.timing(animatedValue, {
      toValue: disabled || isLoading ? 0 : 1,
      duration: 300,
      useNativeDriver: false,
    }).start();
  }, [animatedValue, disabled, isLoading]);

  const interpolatedBackgroundColor = animatedValue.interpolate({
    inputRange: [0, 1],
    outputRange: ["#bdc3c7", backgroundColor], // From disabled to enabled color
  });

  return (
    <Animated.View
      style={[
        { backgroundColor: interpolatedBackgroundColor, borderRadius: 24 },
        style,
      ]}
    >
      <Pressable
        onPress={handlePress}
        disabled={disabled || isLoading}
        className={className}
        style={[styles.pressable]}
      >
        <View style={styles.content}>
          {isLoading && (
            <ActivityIndicator style={styles.spinner} color="#fff" />
          )}
          <Text style={[styles.text, textStyle]}>{children}</Text>
        </View>
      </Pressable>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  pressable: {
    borderRadius: 24,
    paddingVertical: 10,
    paddingHorizontal: 20,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    margin: 5, // Default margin around the button
  },
  text: {
    color: "#fff",
    fontSize: 18,
    fontWeight: "bold",
    textAlign: "center",
    lineHeight: 20, // should match or exceed fontSize
  },
  content: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  spinner: {
    marginRight: 8, // Space between spinner and text
  },
});

export default LoadingButton;
